import React, { useCallback, useEffect, useRef, useState } from 'react';
import RenderUnit, { snapshot } from '../helpers/figures/RenderUnit';
import { fetchCurrent, saveFigure } from '../helpers/figures/figureApi';
import { getDrawingUrl } from '../../config/drawings';
import { showHintPositions, showHintTargets } from '../helpers/cameraPositions';
import { SHAPE_MODEL_MAP } from './Ex1_2026_Shape';
import { GAZE_MODEL_MAP } from './Ex1_2026_Gaze';

// 研究資料（MTG報告・論文）の図版を一括で書き出す開発用ページ（/capture-figures）。
// 学習者には使わせない。演習で扱う全誤りモデルについて次の2枚を作り、
// Flask の POST /capture でリポジトリの research/_captures/ に保存する：
//   drawing_<model>.png … デッサン風画像。学習者が演習で見る public/dessin/<model>_error.png を
//                         そのまま写す（作り直しは /drawing-builder。ここでは描かない）
//   hint3d_<model>.png  … おかしさが顕在化される視点から見た3次元形状モデル
//                         （視点の正本は cameraPositions.js の showHintPositions。
//                          グリッドつき・誤っている箇所を赤で着色）
// 1枚ずつ順番に、キャンバスごと作り直して（key で再マウント）撮る。
const SHEAR_ERROR = 0.5; // 誤りレベル（0=モチーフ一致, 0.2=中間, 0.5=誤り）
// 顕在化視点の「向き」は showHintPositions（正本）のまま、**近すぎるときだけ**この距離まで引く。
// cube_model4 の背面視点だけ極端に近く、そのまま撮るとモデルが画面から見切れるため。
// 引くだけ（寄せない）なので、他のモデルの見え方は変わらない。小さく写る分は
// gen_selfexp_slides.py 側で被写体に合わせて切り出して拡大する。
const MIN_FIGURE_DISTANCE = 500;

const atFigureDistance = (pos) => {
  if (!pos) return pos;
  const len = Math.hypot(pos.x, pos.y, pos.z);
  if (!len || len >= MIN_FIGURE_DISTANCE) return pos;
  const k = MIN_FIGURE_DISTANCE / len;
  return { x: pos.x * k, y: pos.y * k, z: pos.z * k };
};

// 顕在化視点の画像で赤く示す「誤っている箇所」。
// 正本は research/デッサン誤り一覧表_V2.xlsx の「誤っている箇所」列。
// 立方体は辺の組み合わせ単位（CUBE_EDGE_GROUPS）なので、一覧表の記述に最も近い組を当てている。
const ERROR_PART = {
  model1: 'Plate',            // Plate-All（皿全体）
  model2: 'Plate',            // Plate-All
  model3: 'Glass',            // Glass-All
  model4: 'Glass-top',        // Glass-Top（飲み口）
  model5: 'Glass',            // Glass-All
  model6: 'Plate-rim',        // Plate-Rim（ふち）
  model8: 'Plate',            // Plate-All
  model7: 'Glass',            // Glass-All
  cube_model1: 'Cube-topAndSide2', // 上面の奥への辺
  cube_model2: 'Cube-topAndSide1', // 上面の奥への辺（奥の垂直辺が高くなる）
  cube_model3: 'Cube-topAndSide2', // 上面左右奥
  cube_model4: 'Cube-bottomRight', // 下面の右奥への辺
  cube_model5: 'Cube-topAll',      // 上面（Cube-Top）
};

// 演習では扱わないが、プレ／ポスト／遅延テストと報告資料では使うモデル
// （model7＝2026-10-03決定、残り＝2026-10-14に演習を6問・2問へ絞ったぶん。model5 は2026-10-16に model8 と、
//   cube_model3 は2026-10-19に cube_model5 と入れ替え）
const EXTRA_MODELS = ['model3', 'model4', 'model5', 'cube_model2', 'cube_model3', 'cube_model4', 'model7'];
const MODELS = Array.from(new Set([...SHAPE_MODEL_MAP, ...GAZE_MODEL_MAP, ...EXTRA_MODELS]));

// ★参照を固定する★：毎レンダーで新しい座標オブジェクトを渡すと useThreeRenderer が
// シーンを作り直し続けて ready にならないため、ここで一度だけ作る
const FIGURE_POSITIONS = Object.fromEntries(
  MODELS.map((m) => [m, atFigureDistance(showHintPositions[m])])
);
const QUEUE = MODELS.flatMap((glbPath) => [
  { glbPath, kind: 'drawing' },
  { glbPath, kind: 'hint3d' },
]);

export default function CaptureFigures() {
  const [index, setIndex] = useState(0);
  const [log, setLog] = useState([]);
  const busyRef = useRef(false);
  const item = QUEUE[index];
  const done = index >= QUEUE.length;

  // drawing は public/dessin/ の画像を写すだけ、hint3d は描画して撮る
  const handleReady = useCallback(async (three) => {
    if (busyRef.current) return;
    busyRef.current = true;
    const { glbPath, kind } = QUEUE[index];
    const name = `${kind}_${glbPath}.png`;
    try {
      const blob = kind === 'drawing'
        ? await fetchCurrent(getDrawingUrl(glbPath, 'error'))
        : await snapshot(three);
      if (!blob) throw new Error('public/dessin/ に画像がありません（/drawing-builder で作成）');
      await saveFigure(blob, name);
      setLog((prev) => [...prev, `OK   ${name}`]);
    } catch (err) {
      setLog((prev) => [...prev, `NG   ${name}  (${err.message})`]);
    } finally {
      busyRef.current = false;
      setIndex((i) => i + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  useEffect(() => {
    if (item && item.kind === 'drawing') handleReady(null);
  }, [item, handleReady]);

  return (
    <div style={{ padding: 16, fontFamily: 'sans-serif' }}>
      <h1 style={{ fontSize: 18 }}>研究資料の図版書き出し（開発用）</h1>
      <p>
        {done
          ? `完了：${log.filter((l) => l.startsWith('OK')).length} / ${QUEUE.length} 枚を research/_captures/ に保存しました`
          : `${index + 1} / ${QUEUE.length} 枚目を処理中：${item.glbPath}（${item.kind}）`}
      </p>
      {done && <div id="capture-done" data-count={log.filter((l) => l.startsWith('OK')).length} />}
      {!done && item.kind === 'hint3d' && (
        <RenderUnit
          key={`${item.glbPath}_${item.kind}`}
          glbPath={item.glbPath}
          shearValue={SHEAR_ERROR}
          showGrid // 空中に浮いている等の位置関係が分かるようにグリッド・軸を出す
          addColor={ERROR_PART[item.glbPath]} // 誤っている箇所を赤で示す
          cameraPosition={FIGURE_POSITIONS[item.glbPath]}
          cameraTarget={showHintTargets[item.glbPath]}
          onReady={handleReady}
        />
      )}
      <pre style={{ fontSize: 12, lineHeight: 1.5, marginTop: 12 }}>{log.join('\n')}</pre>
    </div>
  );
}
